import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import {
  applyModelOpacity,
  calculateHeadingRotation,
  createBoundaryVisualizer,
  createHeadingArrow,
  georeferenceGlbModel,
  type ScaleFitMode,
  type Vector3Like,
} from "./glbGeoreference";
import { disposeSceneResources } from "./sceneResources";

export type GlbGeoReferencePreviewCanvasProps = Readonly<{
  /** Đường dẫn URL hoặc blob URL của mô hình 3D GLB */
  modelUrl?: string | undefined;
  /** Hoặc truyền trực tiếp Object3D Three.js nếu đã được tải trước */
  modelObject?: THREE.Object3D | undefined;
  /** Góc phương vị cạnh mặt tiền tính theo độ (0 - 360, từ hướng Bắc) */
  bearingDeg?: number | undefined;
  /** Tỉ lệ uniform scale thủ công (nếu không truyền, tự tính từ targetDimensions) */
  scale?: number | undefined;
  /** Kích thước thực tế của khu đất (mét) để tự động scale mô hình */
  targetDimensions?:
    | Readonly<{
        widthMeters?: number | undefined;
        lengthMeters?: number | undefined;
      }>
    | undefined;
  /** Tỉ lệ số mét trên 1 đơn vị scene */
  metersPerSceneUnit?: number | undefined;
  /** Chế độ co giãn: contain (mặc định), cover, width, length */
  fitMode?: ScaleFitMode | undefined;
  /** Độ trong suốt của mô hình (0.0 - 1.0) điều khiển bởi Slider */
  opacity?: number | undefined;
  /** Chiều rộng khung canvas (mặc định '100%') */
  width?: string | number | undefined;
  /** Chiều cao khung canvas (mặc định 400) */
  height?: string | number | undefined;
  /** Tên lớp CSS bổ sung */
  className?: string | undefined;
  /** Góc lệch mặt tiền mặc định của model so với trục Bắc (-Z) */
  modelFrontOffsetDeg?: number | undefined;
  /** Danh sách 4 điểm góc ranh giới trong hệ tọa độ scene cục bộ */
  boundaryPoints?: readonly { x: number; z: number }[] | undefined;
  /** Bật/tắt hiển thị khung dây ranh giới 4 góc (mặc định true) */
  showBoundary?: boolean | undefined;
  /** Bật/tắt hiển thị mũi tên hướng cổng (mặc định true) */
  showHeadingArrow?: boolean | undefined;
  /** Bật/tắt lưới tọa độ mặt đất (mặc định true) */
  showGrid?: boolean | undefined;
  /** Callback thông báo khi mô hình đã tải và định vị xong */
  onLoaded?: ((meta: { boxSize: Vector3Like; appliedScale: number; bearingDeg: number }) => void) | undefined;
  /** Callback khi xảy ra lỗi tải mô hình */
  onError?: ((error: Error) => void) | undefined;
}>;

/**
 * Kiểm tra xem môi trường hiện tại có hỗ trợ WebGL hay không.
 */
function isWebGLSupported(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
    );
  } catch {
    return false;
  }
}

/**
 * Component Canvas 3D Preview phục vụ bước đối soát mô hình GLB với bản đồ ranh giới thực tế.
 * Tích hợp đầy đủ logic:
 * - Chuẩn hóa pivot về tâm đáy mặt đất (y = 0)
 * - Uniform scale khớp kích thước thực địa
 * - Xoay rotation.y quanh trục đứng theo góc phương vị mặt tiền
 * - Điều khiển opacity động từ Slider
 * - Hiển thị khung dây 4 góc và mũi tên hướng cổng
 */
export function GlbGeoReferencePreviewCanvas(props: GlbGeoReferencePreviewCanvasProps) {
  const {
    modelUrl,
    modelObject,
    bearingDeg = 0,
    scale,
    targetDimensions,
    metersPerSceneUnit,
    fitMode = "contain",
    opacity = 1.0,
    width = "100%",
    height = 400,
    className = "",
    modelFrontOffsetDeg = 0,
    boundaryPoints,
    showBoundary = true,
    showHeadingArrow = true,
    showGrid = true,
    onLoaded,
    onError,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState<string | null>(null);

  // References to active Three.js objects
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const wrapperRef = useRef<THREE.Group | null>(null);
  const boundaryLineRef = useRef<THREE.LineLoop | null>(null);
  const arrowHelperRef = useRef<THREE.ArrowHelper | null>(null);
  const animFrameIdRef = useRef<number | null>(null);

  // Lưu trữ model gốc để re-apply khi scale hoặc targetDimensions thay đổi
  const rawModelRef = useRef<THREE.Object3D | null>(null);

  // Khởi tạo Scene, Camera, Renderer, Controls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!isWebGLSupported()) {
      setErrorState("WebGL không được hỗ trợ trên môi trường này");
      setLoading(false);
      return;
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf1f5f9); // Nền sáng trung tính slate-100
    sceneRef.current = scene;

    const widthPx = container.clientWidth || 600;
    const heightPx = typeof height === "number" ? height : container.clientHeight || 400;

    const camera = new THREE.PerspectiveCamera(45, widthPx / heightPx, 0.1, 1000);
    camera.position.set(0, 35, 60);

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(widthPx, heightPx);
      renderer.shadowMap.enabled = true;
      container.appendChild(renderer.domElement);
      rendererRef.current = renderer;
    } catch (e) {
      const err = e instanceof Error ? e : new Error("Không thể khởi tạo WebGL renderer");
      setErrorState(err.message);
      setLoading(false);
      onError?.(err);
      return;
    }

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.maxPolarAngle = Math.PI / 2 - 0.02; // Không cho camera chui xuống dưới mặt đất
    controlsRef.current = controls;

    // Ánh sáng (Lighting)
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.85);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(30, 60, 40);
    dirLight.castShadow = true;
    scene.add(dirLight);

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0x94a3b8, 0.4);
    scene.add(hemiLight);

    // Lưới mặt đất
    if (showGrid) {
      const grid = new THREE.GridHelper(100, 50, 0x94a3b8, 0xcbd5e1);
      grid.position.y = 0;
      scene.add(grid);
    }

    // Vòng lặp render
    let isRunning = true;
    const animate = () => {
      if (!isRunning) return;
      controls.update();
      renderer.render(scene, camera);
      animFrameIdRef.current = requestAnimationFrame(animate);
    };
    animate();

    // Xử lý Resize
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        const h = entry.contentRect.height;
        if (w > 0 && h > 0) {
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
        }
      }
    });
    resizeObserver.observe(container);

    return () => {
      isRunning = false;
      resizeObserver.disconnect();
      if (animFrameIdRef.current !== null) {
        cancelAnimationFrame(animFrameIdRef.current);
      }
      controls.dispose();
      disposeSceneResources(scene);
      renderer.dispose();
      if (renderer.domElement.parentElement) {
        renderer.domElement.parentElement.removeChild(renderer.domElement);
      }
    };
  }, [showGrid, height, onError]);

  // Tải hoặc gắn mô hình 3D vào scene
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    let active = true;

    const setupModel = (model: THREE.Object3D) => {
      if (!active) return;

      // Xóa wrapper cũ nếu có
      if (wrapperRef.current) {
        scene.remove(wrapperRef.current);
        wrapperRef.current = null;
      }

      rawModelRef.current = model;

      const georefResult = georeferenceGlbModel({
        model,
        bearingDeg,
        scale,
        targetDimensions,
        metersPerSceneUnit,
        fitMode,
        opacity,
        modelFrontOffsetDeg,
      });

      wrapperRef.current = georefResult.wrapper;
      scene.add(georefResult.wrapper);

      // Căn camera bao quát mô hình
      const maxDim = Math.max(
        georefResult.bounds.size.x * georefResult.appliedScale,
        georefResult.bounds.size.z * georefResult.appliedScale,
        10,
      );
      if (controlsRef.current) {
        const camera = controlsRef.current.object as THREE.PerspectiveCamera;
        camera.position.set(0, maxDim * 1.1, maxDim * 1.6);
        controlsRef.current.target.set(0, (georefResult.bounds.size.y * georefResult.appliedScale) / 3, 0);
        controlsRef.current.update();
      }

      setLoading(false);
      onLoaded?.({
        boxSize: georefResult.bounds.size,
        appliedScale: georefResult.appliedScale,
        bearingDeg,
      });
    };

    if (modelObject) {
      setupModel(modelObject.clone());
    } else if (modelUrl) {
      setLoading(true);
      const loader = new GLTFLoader();
      loader.load(
        modelUrl,
        (gltf) => {
          setupModel(gltf.scene);
        },
        undefined,
        (err) => {
          if (!active) return;
          const error = err instanceof Error ? err : new Error(`Lỗi tải model: ${modelUrl}`);
          setErrorState(error.message);
          setLoading(false);
          onError?.(error);
        },
      );
    } else {
      // Mặc định tạo một mô hình placeholder hình khối nghĩa trang nếu không có URL
      const placeholder = createCemeteryPlaceholderMesh();
      setupModel(placeholder);
    }

    return () => {
      active = false;
    };
  }, [
    modelUrl,
    modelObject,
    bearingDeg,
    scale,
    targetDimensions,
    metersPerSceneUnit,
    fitMode,
    opacity,
    modelFrontOffsetDeg,
    onLoaded,
    onError,
  ]);

  // Phản hồi độ nhạy tức thì khi thay đổi góc phương vị mặt tiền (bearingDeg)
  useEffect(() => {
    if (wrapperRef.current) {
      const rotY = calculateHeadingRotation(bearingDeg, { modelFrontOffsetDeg });
      wrapperRef.current.rotation.y = rotY;
    }
  }, [bearingDeg, modelFrontOffsetDeg]);

  // Phản hồi tức thì khi thay đổi thanh trượt opacity
  useEffect(() => {
    if (wrapperRef.current) {
      applyModelOpacity(wrapperRef.current, opacity);
    }
  }, [opacity]);

  // Cập nhật khung dây ranh giới 4 góc
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (boundaryLineRef.current) {
      scene.remove(boundaryLineRef.current);
      boundaryLineRef.current.geometry.dispose();
      boundaryLineRef.current = null;
    }

    if (showBoundary && boundaryPoints && boundaryPoints.length >= 3) {
      const line = createBoundaryVisualizer(boundaryPoints);
      boundaryLineRef.current = line;
      scene.add(line);
    }
  }, [boundaryPoints, showBoundary]);

  // Cập nhật mũi tên chỉ hướng cổng
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (arrowHelperRef.current) {
      scene.remove(arrowHelperRef.current);
      arrowHelperRef.current = null;
    }

    if (showHeadingArrow) {
      const arrow = createHeadingArrow(bearingDeg, 18);
      arrowHelperRef.current = arrow;
      scene.add(arrow);
    }
  }, [bearingDeg, showHeadingArrow]);

  return (
    <div
      style={{
        position: "relative",
        width,
        height,
        borderRadius: 8,
        overflow: "hidden",
        border: "1px solid #cbd5e1",
        backgroundColor: "#f8fafc",
      }}
      className={`glb-georef-preview-container ${className}`}
      data-testid="glb-georef-preview-canvas"
    >
      <div
        ref={containerRef}
        style={{ width: "100%", height: "100%", display: errorState ? "none" : "block" }}
      />

      {loading && !errorState && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            backdropFilter: "blur(2px)",
            zIndex: 10,
            fontSize: 14,
            color: "#475569",
            fontWeight: 500,
          }}
        >
          Đang chuẩn hóa và dựng mô hình 3D...
        </div>
      )}

      {errorState && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
            backgroundColor: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
          }}
          data-testid="glb-preview-fallback"
        >
          <div style={{ fontWeight: 600, color: "#334155", marginBottom: 6 }}>
            Xem trước mô hình 3D
          </div>
          <div style={{ fontSize: 13, marginBottom: 12 }}>{errorState}</div>
          <div
            style={{
              fontSize: 12,
              background: "#e2e8f0",
              padding: "6px 12px",
              borderRadius: 4,
              color: "#334155",
            }}
          >
            Phương vị mặt tiền: <strong>{bearingDeg.toFixed(1)}°</strong> | Độ mờ:{" "}
            <strong>{Math.round(opacity * 100)}%</strong>
          </div>
        </div>
      )}

      <div
        style={{
          position: "absolute",
          bottom: 8,
          left: 12,
          backgroundColor: "rgba(15, 23, 42, 0.75)",
          color: "#f8fafc",
          padding: "4px 10px",
          borderRadius: 4,
          fontSize: 12,
          pointerEvents: "none",
          display: "flex",
          gap: 12,
        }}
      >
        <span>
          Hướng cổng: <strong>{bearingDeg.toFixed(1)}°</strong>
        </span>
        <span>
          Độ mờ: <strong>{Math.round(opacity * 100)}%</strong>
        </span>
      </div>
    </div>
  );
}

/**
 * Tạo một mô hình đại diện 3D khuôn viên nghĩa trang (cổng, đài tưởng niệm, các dãy mộ).
 */
function createCemeteryPlaceholderMesh(): THREE.Group {
  const group = new THREE.Group();
  group.name = "cemetery_placeholder_model";

  // Tấm đế khuôn viên
  const baseGeo = new THREE.BoxGeometry(40, 0.5, 60);
  const baseMat = new THREE.MeshStandardMaterial({ color: 0x94a3b8, roughness: 0.8 });
  const base = new THREE.Mesh(baseGeo, baseMat);
  base.position.set(0, 0.25, 0);
  group.add(base);

  // Cổng chính (ở mặt Nam +Z, nhìn ra)
  const gatePillarGeo = new THREE.BoxGeometry(2, 6, 2);
  const gateMat = new THREE.MeshStandardMaterial({ color: 0xb91c1c });
  const leftPillar = new THREE.Mesh(gatePillarGeo, gateMat);
  leftPillar.position.set(-6, 3, 28);
  const rightPillar = new THREE.Mesh(gatePillarGeo, gateMat);
  rightPillar.position.set(6, 3, 28);
  group.add(leftPillar);
  group.add(rightPillar);

  // Đài tưởng niệm ở trung tâm
  const monumentGeo = new THREE.CylinderGeometry(1.5, 2.5, 12, 8);
  const monumentMat = new THREE.MeshStandardMaterial({ color: 0xd97706 });
  const monument = new THREE.Mesh(monumentGeo, monumentMat);
  monument.position.set(0, 6, -5);
  group.add(monument);

  return group;
}

export default GlbGeoReferencePreviewCanvas;
